import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { get, isEmpty, map } from 'lodash-es'
import { intl } from 'lang/intl'
import { useCopyToClipboard } from 'react-use'

import { TtIconButton } from '@23/react'

import {
  Button,
  If,
  MetricsBarComponent,
  MetricSingleComponent,
} from '../../../sharedLegacy/components'
import { ScheduleTimerComponent } from '..'
import { showNotice } from '../../../Layout/Notices/Notices'

import './WebinarOverlayComponent.scss'

const getHeading = (properties) => {
  const {
    isProducer,
    webinarViewLink: link,
    isPresenterAloneBeforeWebinar,
    schedule,
    onLiveCountdownZero,
    onCopyWebinarLink,
  } = properties

  if (isProducer) {
    return (
      <>
        <div className="name">
          <FormattedMessage defaultMessage="Congratulations!" />
        </div>
        <p className="webinar-ended-message">
          <FormattedMessage defaultMessage="Your webinar ended successfully" />
        </p>
      </>
    )
  }

  return (
    <>
      <div className="heading-wrapper">
        <div className="name">
          {isPresenterAloneBeforeWebinar
            ? intl.formatMessage({
                defaultMessage: 'Hey! Your webinar will start in',
              })
            : intl.formatMessage({
                defaultMessage: 'Hey! This webinar has ended.',
              })}
        </div>
        {isPresenterAloneBeforeWebinar && (
          <ScheduleTimerComponent
            startTimeEpoch={get(schedule, 'start_time_epoch')}
            onLiveCountdownZero={onLiveCountdownZero}
            renderOnlyTimer
          />
        )}
      </div>
      {isPresenterAloneBeforeWebinar && (
        <p className="help-text">
          <FormattedMessage defaultMessage="You can prepare yourself until that time to make sure things will run smoothly." />
        </p>
      )}
      <If condition={!isEmpty(link)}>
        <div className="copy-link-text">
          <div>
            <FormattedMessage defaultMessage="Copy the link of your webinar so you can share it." />
          </div>
          <div className="link-wrapper">
            <a
              href={link}
              className="link"
              rel="noopener noreferrer"
              target="_blank"
            >
              {link}
            </a>
            <TtIconButton
              className="audience-link-copy-button"
              label={intl.formatMessage({
                defaultMessage: 'Copy the webinar audience URL to clipboard',
              })}
              name="duplicate"
              library="one"
              onClick={onCopyWebinarLink}
            />
          </div>
        </div>
      </If>
    </>
  )
}

const WebinarOverlayComponent = ({
  isProducer,
  localParticipantName: name,
  webinarOverMetrics,
  areWebinarOverMetricsVisible: areMetricsVisible,
  webinarViewLink,
  redirectToManagePage,
  isPresenterAloneBeforeWebinar,
  schedule,
  onLiveCountdownZero,
}) => {
  const [, copyToClipboard] = useCopyToClipboard()

  const handleCopyWebinarLink = useCallback(() => {
    if (!webinarViewLink) {
      return
    }

    copyToClipboard(webinarViewLink)
    showNotice({
      expirationMillis: 2000,
      isClosable: true,
      message: intl.formatMessage({
        defaultMessage: 'Webinar link copied to clipboard',
      }),
    })
  }, [copyToClipboard, webinarViewLink])

  return (
    <div className="webinar-over-overlay-component">
      <div className="content-wrapper">
        <div className="heading">
          {getHeading({
            isProducer,
            name,
            webinarViewLink,
            areMetricsVisible,
            isPresenterAloneBeforeWebinar,
            schedule,
            onLiveCountdownZero,
            onCopyWebinarLink: handleCopyWebinarLink,
          })}
        </div>
        <If condition={isProducer}>
          <div className="over-overlay-body-wrapper">
            <If condition={areMetricsVisible}>
              <div className="metrics-wrapper">
                <MetricsBarComponent hideBorders>
                  {map(webinarOverMetrics, (metric) => (
                    <MetricSingleComponent
                      key={metric.title}
                      {...metric}
                      titlePosition="bottom"
                      textAlign="center"
                    />
                  ))}
                </MetricsBarComponent>
              </div>
            </If>
            <Button color="transparent" onClick={redirectToManagePage}>
              <FormattedMessage defaultMessage="Go To Manage Page" />
            </Button>
          </div>
        </If>
      </div>
    </div>
  )
}

WebinarOverlayComponent.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  webinarOverMetrics: PropTypes.arrayOf(PropTypes.object),
  localParticipantName: PropTypes.string,
  isProducer: PropTypes.bool,
  areWebinarOverMetricsVisible: PropTypes.bool,
  webinarViewLink: PropTypes.string,
  redirectToManagePage: PropTypes.func,
  isPresenterAloneBeforeWebinar: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  schedule: PropTypes.object,
  onLiveCountdownZero: PropTypes.func,
}

// eslint-disable-next-line import-x/no-default-export
export default WebinarOverlayComponent
